import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { setPageSEO } from "../utils/seo";
import {
  ArrowLeft, FileText, Clock, Zap, ChevronRight,
  CheckCircle, XCircle, Quote, Lightbulb, Eye, EyeOff, BookOpen, Award,
  RotateCcw, Flag, Share2, UserPlus
} from "lucide-react";
import { Button } from "../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const MASCOT_IMAGE = "https://customer-assets.emergentagent.com/job_a00bdaaa-d343-44f1-adb9-f8b11a8d38bf/artifacts/m964dse2_9C7E8ACF-CDB9-4A1C-A556-DF501523912B.jpeg";

export default function DemoSection() {
  const navigate = useNavigate();
  const [section, setSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizResults, setQuizResults] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [shuffledOptions, setShuffledOptions] = useState([]);
  const [showAnswers, setShowAnswers] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [showCta, setShowCta] = useState(false);
  const [startTime] = useState(Date.now());

  useEffect(() => {
    document.title = "Free Bitcoin Education — Try Bitcoin Brain's Course Free";
    setPageSEO({
      title: "Free Bitcoin Education — Try Bitcoin Brain's Course Free",
      description: "Try Section 1 of Bitcoin Brain for free. Learn why Bitcoin exists — plain English, no signup required. The best way to start your Bitcoin education.",
      path: "/demo"
    });
  }, []);

  useEffect(() => {
    axios.get(`${API}/demo/section`).then(res => {
      setSection(res.data);
      if (res.data.quiz_questions) {
        const shuffled = res.data.quiz_questions.map(q => {
          const opts = [q.correct, ...q.wrong];
          for (let i = opts.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [opts[i], opts[j]] = [opts[j], opts[i]];
          }
          return opts;
        });
        setShuffledOptions(shuffled);
      }
      setLoading(false);
    }).catch(() => {
      toast.error("Failed to load demo");
      setLoading(false);
    });
  }, []);

  const handleQuizAnswer = (qIdx, answer) => {
    if (quizSubmitted) return;
    setQuizAnswers(prev => ({ ...prev, [qIdx]: answer }));
  };

  const submitQuiz = async () => {
    if (Object.keys(quizAnswers).length < 3) {
      toast.error("Please answer all questions");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/demo/quiz/submit`, {
        section_id: 1,
        answers: Object.entries(quizAnswers).map(([idx, ans]) => ({
          question_index: parseInt(idx), answer: ans
        })),
        total_time_spent: Math.floor((Date.now() - startTime) / 1000)
      });
      setQuizResults(res.data);
      setQuizSubmitted(true);
      setTimeout(() => setShowResult(true), 300);
      setTimeout(() => setShowCta(true), 1200);
    } catch {
      toast.error("Failed to submit quiz");
    } finally {
      setSubmitting(false);
    }
  };

  const shareDemo = () => {
    const url = window.location.origin + "/demo";
    const text = `I just tried Bitcoin Brain's free demo lesson — "Why Bitcoin Exists." Try it yourself!`;
    if (navigator.share) {
      navigator.share({ title: "Bitcoin Brain Demo", text, url });
    } else {
      navigator.clipboard.writeText(`${text}\n${url}`);
      toast.success("Link copied!");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#F7931A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!section) return null;

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="demo-page">
      {/* Demo Banner */}
      <div className="bg-gradient-to-r from-[#F7931A]/20 to-[#F7931A]/5 border-b border-[#F7931A]/30 py-2 px-4 text-center">
        <p className="text-[#F7931A] text-sm font-medium">
          Try Bitcoin Brain's Course Free — Section 1 of 21 | <button onClick={() => navigate("/register")} className="underline hover:text-white transition-colors">Sign up to unlock all sections</button>
        </p>
      </div>

      <header className="sticky top-0 z-50 glass-darker border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors" data-testid="demo-back-btn">
            <ArrowLeft size={20} />
            <span className="hidden sm:inline">Home</span>
          </button>
          <div className="flex items-center gap-3">
            <Button onClick={shareDemo} variant="ghost" size="sm" className="text-zinc-400 hover:text-[#F7931A]" data-testid="demo-share-btn">
              <Share2 size={16} className="mr-1" /> Share
            </Button>
            <Button onClick={() => navigate("/register")} size="sm" className="btn-primary" data-testid="demo-signup-btn">
              <UserPlus size={16} className="mr-1" /> Sign Up
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <AnimatePresence mode="wait">
          {!showQuiz ? (
            <motion.div key="content" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-2">
                  <span className="badge-orange">Module {section.module}</span>
                  <span className="text-zinc-500 text-sm">Demo — Section 1 of 21</span>
                </div>
                <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">{section.title}</h1>
                <p className="text-xl text-[#F7931A] italic">"{section.hook}"</p>
              </div>

              <div className="prose prose-invert max-w-none mb-8">
                <p className="text-zinc-300 text-lg leading-relaxed whitespace-pre-line">{section.content}</p>
              </div>

              {section.influence_card && (
                <div className="bg-[#0A0A0A] border-l-4 border-[#F7931A] p-6 rounded-r-xl mb-8">
                  <Quote className="text-[#F7931A] mb-3" size={24} />
                  <p className="text-white text-lg italic mb-3">"{section.influence_card.quote}"</p>
                  <p className="text-[#F7931A] font-medium">— {section.influence_card.name}</p>
                </div>
              )}

              <div className="bg-gradient-to-br from-[#F7931A]/10 to-transparent border border-[#F7931A]/30 rounded-xl p-6 mb-8">
                <div className="flex items-start gap-4">
                  <img src={MASCOT_IMAGE} alt="Bryce" className="w-12 h-12 rounded-lg flex-shrink-0" />
                  <div>
                    <h3 className="font-heading text-lg font-bold text-white mb-2">Reality Check</h3>
                    <p className="text-zinc-300 italic">{section.reality_check}</p>
                    <p className="text-zinc-500 text-sm mt-2">— Bryce Fields</p>
                  </div>
                </div>
              </div>

              <div className="text-center py-8">
                <p className="text-zinc-500 mb-4">Ready to test your understanding?</p>
                <Button onClick={() => setShowQuiz(true)} className="btn-primary text-lg px-8 py-4" data-testid="demo-take-quiz-btn">
                  <BookOpen className="mr-2" size={20} /> Take the Demo Quiz
                </Button>
              </div>
            </motion.div>
          ) : (
            <motion.div key="quiz" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="mb-8">
                <button onClick={() => { setShowQuiz(false); setQuizSubmitted(false); setQuizAnswers({}); setQuizResults(null); setShowResult(false); setShowCta(false); setShowAnswers(false); }}
                  className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-4">
                  <ArrowLeft size={18} /> Back to lesson
                </button>
                <h2 className="font-heading text-3xl font-bold text-white mb-2">Demo Quiz</h2>
                <p className="text-zinc-500">Answer correctly to see how Bitcoin Brain works. 2 of 3 to pass.</p>
              </div>

              <div className="space-y-6">
                {section.quiz_questions.map((question, qIndex) => {
                  const options = shuffledOptions[qIndex] || [question.correct, ...question.wrong];
                  const selectedAnswer = quizAnswers[qIndex];
                  const showCorrect = quizSubmitted && showAnswers && options.find(o => o === question.correct) === options.find(o => o === question.correct);
                  return (
                    <div key={qIndex} className={`bg-[#0A0A0A] border rounded-xl p-6 transition-colors duration-500 ${
                      quizSubmitted && showAnswers && selectedAnswer === question.correct ? "border-green-500/30" :
                      quizSubmitted && showAnswers && selectedAnswer && selectedAnswer !== question.correct ? "border-red-500/30" : "border-white/10"
                    }`}>
                      <p className="text-white font-medium mb-4">{qIndex + 1}. {question.question}</p>
                      <div className="space-y-3">
                        {options.map((option, oIndex) => {
                          const isSelected = selectedAnswer === option;
                          const isCorrect = quizSubmitted && showAnswers && option === question.correct;
                          const isWrong = quizSubmitted && showAnswers && isSelected && option !== question.correct;
                          return (
                            <button key={oIndex} onClick={() => handleQuizAnswer(qIndex, option)} disabled={quizSubmitted}
                              className={`w-full p-4 rounded-lg border text-left transition-colors ${
                                isCorrect ? "border-green-500 bg-green-500/20 text-green-400" :
                                isWrong ? "border-red-500 bg-red-500/20 text-red-400" :
                                isSelected ? "border-[#F7931A] bg-[#F7931A]/10 text-white" :
                                "border-white/10 bg-[#161616] text-zinc-300 hover:border-[#F7931A]/50"
                              }`}
                              data-testid={`demo-quiz-option-${qIndex}-${oIndex}`}>
                              <span className="flex items-center gap-3">
                                {isCorrect && <CheckCircle size={18} />}
                                {isWrong && <XCircle size={18} />}
                                {option}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-8 space-y-4">
                {!quizSubmitted ? (
                  <Button onClick={submitQuiz} disabled={submitting || Object.keys(quizAnswers).length < 3}
                    className="w-full btn-primary text-lg py-4" data-testid="demo-submit-quiz-btn">
                    {submitting ? "Submitting..." : "Submit Quiz"}
                  </Button>
                ) : (
                  <>
                    <AnimatePresence>
                      {showResult && (
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                          className={`p-6 rounded-xl ${quizResults?.passed ? 'bg-green-500/20 border border-green-500/30' : 'bg-red-500/20 border border-red-500/30'}`}>
                          <div className="flex items-center gap-3 mb-2">
                            {quizResults?.passed ? <CheckCircle className="text-green-400" size={24} /> : <XCircle className="text-red-400" size={24} />}
                            <h3 className={`font-heading text-xl font-bold ${quizResults?.passed ? 'text-green-400' : 'text-red-400'}`}>
                              {quizResults?.passed ? "Quiz Passed!" : "Keep Learning"}
                            </h3>
                          </div>
                          <p className="text-zinc-300">You got {quizResults?.correct} of {quizResults?.total} correct.</p>
                          <p className="text-zinc-500 text-sm mt-2">{quizResults?.message}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {showResult && (
                      <Button onClick={() => setShowAnswers(!showAnswers)} variant="ghost"
                        className="w-full text-zinc-400 hover:text-white border border-white/10" data-testid="demo-toggle-answers-btn">
                        {showAnswers ? <EyeOff size={16} className="mr-2" /> : <Eye size={16} className="mr-2" />}
                        {showAnswers ? "Hide Answers" : "Show Answers"}
                      </Button>
                    )}

                    <AnimatePresence>
                      {showCta && (
                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
                          <div className="bg-gradient-to-br from-[#F7931A]/20 to-[#F7931A]/5 border border-[#F7931A]/30 rounded-xl p-6 text-center">
                            <img src={MASCOT_IMAGE} alt="Bitcoin Brain" className="w-16 h-16 rounded-xl mx-auto mb-3 btc-glow" />
                            <h3 className="font-heading text-xl font-bold text-white mb-2">Unlock All 21 Sections</h3>
                            <p className="text-zinc-400 text-sm mb-4">Sign up to track your BTC XP, earn badges, and complete the full Bitcoin Brain course.</p>
                            <div className="flex gap-3 justify-center">
                              <Button onClick={() => navigate("/register")} className="btn-primary" data-testid="demo-cta-signup">
                                <UserPlus size={16} className="mr-2" /> Create Free Account
                              </Button>
                              <Button onClick={shareDemo} variant="ghost" className="text-zinc-400 hover:text-[#F7931A]" data-testid="demo-cta-share">
                                <Share2 size={16} className="mr-2" /> Share Demo
                              </Button>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
